/**
 * Form for creating/editing training samples.
 * Supports text input, image upload (transcribed via AI), and voice input.
 */
import { useState, useEffect, useRef } from 'react'
import { Sample, SampleCreate, apiClient } from '@/services/api'

interface SampleFormProps {
  sample?: Sample | null
  onSubmit: (data: SampleCreate) => void
  onCancel: () => void
  isSubmitting: boolean
}

function useVoiceInput(onResult: (text: string) => void) {
  const [isListening, setIsListening] = useState(false)
  const [supported] = useState(
    () => !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition
  )
  const recognitionRef = useRef<any>(null)

  const toggle = () => {
    if (isListening) {
      recognitionRef.current?.stop()
      return
    }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const recognition = new SR()
    recognition.continuous = true
    recognition.interimResults = false
    recognition.lang = 'en-US'
    recognition.onresult = (e: any) => {
      const transcript = Array.from(e.results)
        .map((r: any) => r[0].transcript)
        .join(' ')
      onResult(transcript)
    }
    recognition.onend = () => setIsListening(false)
    recognition.start()
    recognitionRef.current = recognition
    setIsListening(true)
  }

  return { isListening, supported, toggle }
}

export const SampleForm: React.FC<SampleFormProps> = ({
  sample,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [notesText, setNotesText] = useState('')
  const [reportText, setReportText] = useState('')
  const [isTranscribingNotes, setIsTranscribingNotes] = useState(false)
  const [isTranscribingReport, setIsTranscribingReport] = useState(false)
  const [transcribeError, setTranscribeError] = useState<string | null>(null)
  const [notesImagePreview, setNotesImagePreview] = useState<string | null>(null)
  const [reportImagePreview, setReportImagePreview] = useState<string | null>(null)
  const notesFileRef = useRef<HTMLInputElement>(null)
  const notesCameraRef = useRef<HTMLInputElement>(null)
  const reportFileRef = useRef<HTMLInputElement>(null)
  const reportCameraRef = useRef<HTMLInputElement>(null)

  const notesVoice = useVoiceInput((text) => setNotesText((prev) => prev ? `${prev} ${text}` : text))
  const reportVoice = useVoiceInput((text) => setReportText((prev) => prev ? `${prev} ${text}` : text))

  useEffect(() => {
    if (sample) {
      setNotesText(sample.notes_text)
      setReportText(sample.report_text)
    } else {
      setNotesText('')
      setReportText('')
    }
    setNotesImagePreview(null)
    setReportImagePreview(null)
    setTranscribeError(null)
  }, [sample])

  const handleImageUpload = async (
    file: File,
    setPreview: (url: string | null) => void,
    setLoading: (v: boolean) => void,
    setText: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setTranscribeError(null)
    setPreview(URL.createObjectURL(file))
    setLoading(true)
    try {
      const result = await apiClient.transcribeImage(file)
      setText((prev) => prev ? `${prev}\n${result.transcribed_text}` : result.transcribed_text)
    } catch (err: any) {
      setTranscribeError(err.message || 'Failed to transcribe image')
      setPreview(null)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!notesText.trim() || !reportText.trim()) {
      alert('Both notes and report are required')
      return
    }
    onSubmit({ notes_text: notesText, report_text: reportText })
  }

  const isWorking = isTranscribingNotes || isTranscribingReport || isSubmitting

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {sample ? 'Edit Training Sample' : 'Add Training Sample'}
            </h2>
            <p className="text-gray-600 mt-2">
              Provide an example of your meeting notes and the corresponding report.
              You can type, upload a photo, or use your voice.
            </p>
          </div>

          {transcribeError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {transcribeError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Notes Field */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="notes" className="label mb-0">
                  Example Meeting Notes
                </label>
                <div className="flex gap-2">
                  {notesVoice.supported && (
                    <button
                      type="button"
                      onClick={notesVoice.toggle}
                      disabled={isWorking}
                      title={notesVoice.isListening ? 'Stop recording' : 'Dictate notes'}
                      className={`btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5 ${notesVoice.isListening ? 'bg-red-100 border-red-300 text-red-700' : ''}`}
                    >
                      <svg className={`h-4 w-4 ${notesVoice.isListening ? 'animate-pulse' : ''}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                      {notesVoice.isListening ? 'Stop' : 'Voice'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => notesCameraRef.current?.click()}
                    disabled={isWorking}
                    className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
                  >
                    {isTranscribingNotes ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Transcribing...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        Camera
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => notesFileRef.current?.click()}
                    disabled={isWorking}
                    className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    Upload Photo
                  </button>
                </div>
              </div>
              <input
                ref={notesFileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleImageUpload(file, setNotesImagePreview, setIsTranscribingNotes, setNotesText)
                  e.target.value = ''
                }}
              />
              <input
                ref={notesCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleImageUpload(file, setNotesImagePreview, setIsTranscribingNotes, setNotesText)
                  e.target.value = ''
                }}
              />
              {notesImagePreview && !isTranscribingNotes && (
                <div className="mb-2 flex items-center gap-2">
                  <img src={notesImagePreview} alt="Notes" className="h-10 w-10 object-cover rounded border border-gray-200"/>
                  <span className="text-xs text-gray-500">Photo transcribed</span>
                  <button type="button" onClick={() => setNotesImagePreview(null)} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                </div>
              )}
              <textarea
                id="notes"
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                className="textarea min-h-[200px]"
                placeholder="Type your notes, upload a photo, or use voice..."
                disabled={isWorking}
                required
              />
              <p className="text-sm text-gray-500 mt-1">{notesText.length} / 10,000 characters</p>
            </div>

            {/* Report Field */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="report" className="label mb-0">
                  Corresponding Report (Your Preferred Format)
                </label>
                <div className="flex gap-2">
                  {reportVoice.supported && (
                    <button
                      type="button"
                      onClick={reportVoice.toggle}
                      disabled={isWorking}
                      title={reportVoice.isListening ? 'Stop recording' : 'Dictate report'}
                      className={`btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5 ${reportVoice.isListening ? 'bg-red-100 border-red-300 text-red-700' : ''}`}
                    >
                      <svg className={`h-4 w-4 ${reportVoice.isListening ? 'animate-pulse' : ''}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                      {reportVoice.isListening ? 'Stop' : 'Voice'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => reportCameraRef.current?.click()}
                    disabled={isWorking}
                    className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
                  >
                    {isTranscribingReport ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Transcribing...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        Camera
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => reportFileRef.current?.click()}
                    disabled={isWorking}
                    className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1.5"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    Upload Photo
                  </button>
                </div>
              </div>
              <input
                ref={reportFileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleImageUpload(file, setReportImagePreview, setIsTranscribingReport, setReportText)
                  e.target.value = ''
                }}
              />
              <input
                ref={reportCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleImageUpload(file, setReportImagePreview, setIsTranscribingReport, setReportText)
                  e.target.value = ''
                }}
              />
              {reportImagePreview && !isTranscribingReport && (
                <div className="mb-2 flex items-center gap-2">
                  <img src={reportImagePreview} alt="Report" className="h-10 w-10 object-cover rounded border border-gray-200"/>
                  <span className="text-xs text-gray-500">Photo transcribed</span>
                  <button type="button" onClick={() => setReportImagePreview(null)} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                </div>
              )}
              <textarea
                id="report"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                className="textarea min-h-[300px]"
                placeholder="Type the polished report, upload a photo of it, or use voice..."
                disabled={isWorking}
                required
              />
              <p className="text-sm text-gray-500 mt-1">{reportText.length} / 20,000 characters</p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button type="button" onClick={onCancel} className="btn-secondary" disabled={isWorking}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isWorking}>
                {isSubmitting ? 'Saving...' : (sample ? 'Update Sample' : 'Add Sample')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
